import { ref, computed } from 'vue'
import { defineStore } from 'pinia'

import childEditionPreviousEditionApi from '@/services/http/api/childEditionPreviousEdition'
import { useAuthStore } from '@/stores/auth'
import { useI18n } from 'vue-i18n'
import { FetchPeselPayloadSchema } from '@/schemas/childEdition'
import { shouldRetryGetActionForAssignedEndpoint } from '@/helpers/retryActionForOtherEndpoint'
import { throwIfInvalidChildEditionId } from '@/helpers/stores/axiosErrorCheck'
import permissions from '@/constants/permissions'
import type { ChildEditionId, FetchChildDataPayload, TODO } from '@/types'

export const useChildEditionPreviousEditionStore = defineStore(
  'childEditionPreviousEdition',
  () => {
    const authStore = useAuthStore()
    const { t } = useI18n()

    const loader = ref<boolean>(false)
    const child = ref<TODO | null>(null)
    const resources = ref<TODO | null>(null)
    const childDataRequestSkipped = ref<boolean>(false)
    const childPesel = ref<string | null>(null)

    const childEditionName = computed<string | null>(() => child.value?.edition?.name ?? null)

    const hasViewPeselPastEditionPermission = computed(() =>
      authStore.userHasPermission(permissions.peselViewPastEdition)
    )

    const clear = () => {
      loader.value = false
      child.value = null
      resources.value = null
      childPesel.value = null
      childDataRequestSkipped.value = false
    }

    const fetchChildData = async (payload: FetchChildDataPayload) => {
      throwIfInvalidChildEditionId(payload?.childEditionId)

      loader.value = true

      try {
        const { data } = await childEditionPreviousEditionApi.get(
          payload,
          payload?.getAssignedFirst
        )
        child.value = data?.data?.child_edition || null

        let parsedResources = null
        if (data?.data?.resources) {
          parsedResources = { ...data.data.resources }
          if (parsedResources?.document_questions?.guardian_consent) {
            parsedResources.document_questions.guardian_consent = {
              ...parsedResources.document_questions.guardian_consent,
              is_street_disabled: {
                label: t('label.isStreet'),
                required: false
              }
            }
          }
        }
        resources.value = parsedResources
      } catch (error) {
        if (shouldRetryGetActionForAssignedEndpoint(error, payload)) {
          await fetchChildData({ ...payload, getAssignedFirst: true })
        } else {
          throw error
        }
      } finally {
        loader.value = false
      }
    }

    const fetchPesel = async (childEditionId: ChildEditionId) => {
      if (!hasViewPeselPastEditionPermission.value) {
        console.warn('User does not have permission to view pesel')
        return
      }

      const payloadValidation = FetchPeselPayloadSchema.safeParse({ childEditionId })
      if (!payloadValidation.success) {
        console.error('[fetchPesel] Zod Validation Error', payloadValidation.error.format())
        return
      }

      loader.value = true

      try {
        const { data } = await childEditionPreviousEditionApi.getPesel(childEditionId)
        childPesel.value = data.data.pesel
      } catch (error) {
        console.error('Failed to fetch pesel', error)
      } finally {
        loader.value = false
      }
    }

    return {
      loader,
      child,
      resources,
      childDataRequestSkipped,
      childPesel,
      childEditionName,
      hasViewPeselPastEditionPermission,
      clear,
      fetchChildData,
      fetchPesel
    }
  }
)
